import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  ArrowLeft, BarChart3, Clock, Target, TrendingUp, 
  Trophy, AlertTriangle, Calendar, Zap
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DrillStats() {
  const { drillId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [drill, setDrill] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDrillStats();
  }, [drillId]);

  const fetchDrillStats = async () => {
    try {
      const response = await axios.get(`${API}/drills/${drillId}`);
      setDrill(response.data);
    } catch (error) {
      toast.error("Failed to load drill stats");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!drill) {
    return null;
  }

  // Prepare chart data
  const chartData = drill.sessions?.slice(0, 20).reverse().map((session, idx) => ({
    session: idx + 1,
    accuracy: session.accuracy || 0,
    time: session.time_per_question || 10
  })) || [];

  // Calculate stats
  const totalSessions = drill.total_sessions || 0;
  const avgAccuracy = drill.sessions?.length > 0
    ? drill.sessions.reduce((sum, s) => sum + (s.accuracy || 0), 0) / drill.sessions.length
    : 0;
  const bestAccuracy = drill.sessions?.length > 0
    ? Math.max(...drill.sessions.map(s => s.accuracy || 0))
    : 0;
  const currentTime = drill.adaptive_time_seconds || 10;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{drill.drill?.name}</h1>
              <p className="text-sm text-gray-500">Drill Statistics & Progress</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalSessions}</p>
                  <p className="text-xs text-gray-500">Sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{Math.round(avgAccuracy)}%</p>
                  <p className="text-xs text-gray-500">Avg Accuracy</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{bestAccuracy}%</p>
                  <p className="text-xs text-gray-500">Best Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{currentTime}s</p>
                  <p className="text-xs text-gray-500">Current Speed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Chart */}
        {chartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                Progress Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="session" 
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Session', position: 'bottom', fontSize: 12 }}
                    />
                    <YAxis 
                      domain={[0, 100]}
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Accuracy %', angle: -90, position: 'insideLeft', fontSize: 12 }}
                    />
                    <Tooltip 
                      contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
                      formatter={(value, name) => [
                        name === 'accuracy' ? `${value}%` : `${value}s`,
                        name === 'accuracy' ? 'Accuracy' : 'Time/Question'
                      ]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="accuracy" 
                      stroke="#6366f1" 
                      strokeWidth={2}
                      dot={{ fill: '#6366f1', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Problem Areas */}
        {drill.problem_areas?.length > 0 && (
          <Card className="border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Problem Areas to Practice
              </CardTitle>
              <CardDescription>
                These multiplication problems are frequently answered incorrectly
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {drill.problem_areas.map((problem, idx) => (
                  <div 
                    key={idx}
                    className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center"
                  >
                    <p className="text-lg font-bold text-amber-800 font-mono">
                      {problem.problem}
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      Wrong {problem.wrong_count}x
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Sessions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Recent Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {drill.sessions?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No sessions completed yet</p>
            ) : (
              <div className="space-y-3">
                {drill.sessions?.slice(0, 10).map((session, idx) => (
                  <div 
                    key={session.session_id || idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`
                        w-12 h-12 rounded-lg flex flex-col items-center justify-center
                        ${session.accuracy >= 90 ? 'bg-green-100 text-green-700' :
                          session.accuracy >= 70 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'}
                      `}>
                        <span className="text-lg font-bold">{session.accuracy}%</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {session.correct_count}/{session.total_questions} correct
                        </p>
                        <p className="text-sm text-gray-500">
                          {session.time_per_question}s per question • 
                          {session.completed_at ? new Date(session.completed_at).toLocaleDateString() : 'In progress'}
                        </p>
                      </div>
                    </div>
                    <Badge 
                      variant={session.accuracy >= 90 ? "default" : "outline"}
                      className={session.accuracy >= 90 ? "bg-green-500" : ""}
                    >
                      {session.accuracy >= 90 ? "Excellent" : 
                       session.accuracy >= 70 ? "Good" : "Practice"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Drill Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Drill Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Tables</p>
                <p className="font-medium">{drill.drill?.tables?.join(", ")}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Multiplier Range</p>
                <p className="font-medium">1 to {drill.drill?.multiplier_range}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Questions per Drill</p>
                <p className="font-medium">
                  {(drill.drill?.tables?.length || 0) * (drill.drill?.multiplier_range || 12)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium">
                  {drill.drill?.created_at ? new Date(drill.drill.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
